import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './Reservations.css';

const db = getFirestore();
const functions = getFunctions();

const DAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Table-reservation booking flow (Clover / Toast / OpenTable pattern):
 * party size → date → time slot → details → book. After booking, the user
 * can add a pre-order from the restaurant's REAL menu (full cart with
 * options) — the checkout knows it's for the reservation and links the
 * regular order document to it (see CheckoutPage reservation mode).
 */
const ReservationBookingPage = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { user, userData, loading: authLoading } = useAuth();

  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  const [partySize, setPartySize] = useState(2);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]); // {time: Date, available, remaining}
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState(userData?.name || user?.displayName || '');
  const [phone, setPhone] = useState(userData?.phone || '');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const rs = store?.reservationSettings || {};
  const maxPartySize = rs.maxPartySize || 12;
  const maxAdvanceDays = rs.maxAdvanceDays || 30;
  const slotInterval = rs.slotIntervalMinutes || 30;
  const minAdvanceHours = rs.minAdvanceHours ?? 2;

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'publicStores', storeId));
        if (snap.exists()) setStore({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error('Error loading store', e);
      }
      setLoading(false);
    };
    load();
  }, [storeId]);

  const generateSlots = useCallback((date) => {
    const raw = store?.operationHours;
    if (!Array.isArray(raw)) return [];
    const dayName = DAY_NAMES[((date.getDay() + 6) % 7)]; // JS Sun=0 → monday-first
    const entry = raw.find((h) => (h.day || '').toLowerCase() === dayName);
    if (!entry || entry.isClosed === true) return [];
    const fromH = entry.fromHour ?? 0;
    const fromM = entry.fromMinute ?? 0;
    const toH = entry.toHour ?? 23;
    const toM = entry.toMinute ?? 59;
    const minStart = new Date(Date.now() + minAdvanceHours * 3600 * 1000);
    const result = [];
    let t = new Date(date.getFullYear(), date.getMonth(), date.getDate(), fromH, fromM);
    const close = new Date(date.getFullYear(), date.getMonth(), date.getDate(), toH, toM);
    while (t <= close) {
      if (t > minStart) result.push(new Date(t));
      t = new Date(t.getTime() + slotInterval * 60000);
    }
    return result;
  }, [store, slotInterval, minAdvanceHours]);

  const checkAvailability = useCallback(async (date) => {
    if (!store) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    const candidates = generateSlots(date);
    let next = candidates.map((t) => ({ time: t, available: true, remaining: 99 }));
    if (candidates.length > 0) {
      try {
        const fn = httpsCallable(functions, 'getReservationAvailability');
        const { data } = await fn({
          businessId: store.businessId,
          locationId: storeId,
          slots: candidates.map((t) => t.toISOString()),
          // Minutes to ADD to UTC to get the restaurant's local time — the
          // server validates operationHours/capacity in local time, not UTC.
          tzOffsetMinutes: -new Date().getTimezoneOffset(),
        });
        if (data.enabled === false) {
          next = [];
        } else {
          const byTime = {};
          (data.slots || []).forEach((s) => { byTime[s.time] = s; });
          next = candidates.map((t) => {
            const s = byTime[t.toISOString()];
            return { time: t, available: s?.available === true, remaining: s?.remaining ?? 0 };
          });
        }
      } catch (e) {
        // Fail-open: availability is enforced server-side at create time.
        console.warn('Availability check failed, failing open', e);
      }
    }
    setSlots(next);
    setLoadingSlots(false);
  }, [generateSlots, store, storeId]);

  useEffect(() => {
    checkAvailability(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, store]);

  const createReservation = async () => {
    const fn = httpsCallable(functions, 'createReservation');
    const { data: result } = await fn({
      businessId: store.businessId,
      locationId: storeId,
      dateTime: selectedSlot.toISOString(),
      partySize,
      notes: notes.trim() || null,
      customerName: name.trim(),
      customerEmail: user?.email || null,
      customerPhone: phone.trim(),
      source: 'public_web',
      autoAssignTable: true,
      tzOffsetMinutes: -new Date().getTimezoneOffset(),
    });
    if (!result.success) throw new Error(result.error || 'Could not create the reservation');
    return result;
  };

  const handleSubmit = async (thenBrowseMenu) => {
    setError(null);
    if (!selectedSlot) return setError('Please pick a time slot');
    if (!name.trim()) return setError('Please enter your name');
    if (phone.trim().replace(/\D/g, '').length < 10) return setError('Please enter a valid phone number');

    // Review & confirm — the user explicitly accepts before the booking is
    // executed (reported UX gap: the button used to book immediately).
    const dateStr = selectedSlot.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    const timeStr = selectedSlot.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const review = await Swal.fire({
      title: 'Confirm your reservation',
      html: `
        <div style="text-align:left;font-size:14px;line-height:1.9;">
          <div><i class="fas fa-utensils" style="color:#ff5722;width:20px;"></i> <strong>${store.locationName}</strong></div>
          <div><i class="fas fa-calendar" style="color:#ff5722;width:20px;"></i> ${dateStr} · ${timeStr}</div>
          <div><i class="fas fa-users" style="color:#ff5722;width:20px;"></i> ${partySize} guests</div>
          <div><i class="fas fa-user" style="color:#ff5722;width:20px;"></i> ${name.trim()}</div>
          <div><i class="fas fa-phone" style="color:#ff5722;width:20px;"></i> ${phone.trim()}</div>
          ${notes.trim() ? `<div><i class="fas fa-sticky-note" style="color:#ff5722;width:20px;"></i> ${notes.trim()}</div>` : ''}
        </div>
        <p style="font-size:12px;color:#6b7280;margin-top:12px;">
          Free cancellation with a full refund any time before you are seated.
        </p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirm reservation',
      cancelButtonText: 'Back',
      confirmButtonColor: '#ff5722',
      cancelButtonColor: '#6b7280',
    });
    if (!review.isConfirmed) return;

    setSubmitting(true);
    try {
      const result = await createReservation();
      if (thenBrowseMenu) {
        // Straight to the real menu in reservation mode — the checkout will
        // link the order to this reservation.
        const slug = (store.locationName || ' restaurant')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
          .substring(0, 60);
        navigate(`/store/${slug}-${storeId}`, {
          state: {
            reservationContext: {
              reservationId: result.reservationId,
              businessId: store.businessId,
              locationId: storeId,
              locationName: store.locationName,
              dateTime: selectedSlot.toISOString(),
              partySize,
            },
          },
        });
      } else {
        await Swal.fire({
          icon: 'success',
          title: result.status === 'pending' ? 'Request sent!' : 'Reservation confirmed!',
          text: result.status === 'pending'
            ? 'The restaurant will confirm your reservation shortly.'
            : `Your confirmation code is ${result.confirmationNumber}`,
          confirmButtonColor: '#ff5722',
        });
        navigate(`/reservation/${result.reservationId}`);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authLoading && !user) {
    return (
      <>
        <Header />
        <div className="ff-resv-page">
          <div className="ff-resv-card" style={{ textAlign: 'center', padding: 40 }}>
            <i className="fas fa-calendar-check" style={{ fontSize: 48, color: '#ff5722' }}></i>
            <h2>Sign in to book a table</h2>
            <p style={{ color: '#6b7280' }}>
              You need an account so the restaurant can confirm your reservation and send you reminders.
            </p>
            <button className="ff-resv-submit" onClick={() => navigate('/explore')}>Back to explore</button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const days = Array.from({ length: Math.min(maxAdvanceDays, 30) }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });

  return (
    <>
      <Header />
      {loading || authLoading ? (
        <div className="ff-resv-page"><p style={{ textAlign: 'center', color: '#6b7280' }}>Loading…</p></div>
      ) : !store ? (
        <div className="ff-resv-page"><p style={{ textAlign: 'center' }}>Store not found</p></div>
      ) : (
        <div className="ff-resv-page">
          <div className="ff-resv-card">
            <div className="ff-resv-store-head">
              {store.logoUrl
                ? <img src={store.logoUrl} alt="" className="ff-resv-store-logo" />
                : <div className="ff-resv-store-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="fas fa-utensils" style={{ color: '#ff5722' }}></i></div>}
              <div>
                <h2 className="ff-resv-store-name">{store.locationName}</h2>
                <p className="ff-resv-store-addr">{store.address}{store.city ? `, ${store.city}` : ''}</p>
              </div>
            </div>
          </div>

          <div className="ff-resv-card">
            <h3 className="ff-resv-section-title">Party size</h3>
            <div className="ff-resv-party">
              <button className="ff-resv-party-btn" disabled={partySize <= 1} onClick={() => setPartySize(partySize - 1)}>−</button>
              <span><i className="fas fa-users" style={{ color: '#ff5722', marginRight: 8 }}></i><strong>{partySize} guests</strong></span>
              <button className="ff-resv-party-btn" disabled={partySize >= maxPartySize} onClick={() => setPartySize(partySize + 1)}>+</button>
            </div>
          </div>

          <div className="ff-resv-card">
            <h3 className="ff-resv-section-title">Date</h3>
            <div className="ff-resv-date-strip">
              {days.map((d, i) => {
                const active = d.toDateString() === selectedDate.toDateString();
                return (
                  <div
                    key={i}
                    className={`ff-resv-date-chip ${active ? 'active' : ''}`}
                    onClick={() => setSelectedDate(d)}
                  >
                    <div className="dow">{d.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                    <div className="day">{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ff-resv-card">
            <h3 className="ff-resv-section-title">Time</h3>
            {loadingSlots ? (
              <p style={{ color: '#6b7280' }}>Checking availability…</p>
            ) : slots.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No availability on this date — try another day.</p>
            ) : (
              <div className="ff-resv-slots">
                {slots.map((s, i) => {
                  const active = selectedSlot && s.time.getTime() === selectedSlot.getTime();
                  return (
                    <button
                      key={i}
                      className={`ff-resv-slot ${active ? 'active' : ''} ${!s.available ? 'disabled' : ''}`}
                      disabled={!s.available}
                      onClick={() => setSelectedSlot(s.time)}
                    >
                      {s.time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="ff-resv-card">
            <h3 className="ff-resv-section-title">Your details</h3>
            <input className="ff-resv-input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <input className="ff-resv-input" placeholder="Phone (for confirmation texts)" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <textarea className="ff-resv-textarea" rows={2} placeholder="Special requests (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {error && <p className="ff-resv-error">{error}</p>}

          <button className="ff-resv-submit" disabled={submitting} onClick={() => handleSubmit(false)}>
            {submitting ? 'Processing…' : 'Book table'}
          </button>
          <button
            className="ff-resv-submit"
            style={{ background: '#fff', color: '#ff5722', border: '1px solid #ff5722', marginTop: 10 }}
            disabled={submitting}
            onClick={() => handleSubmit(true)}
          >
            <i className="fas fa-utensils" style={{ marginRight: 8 }}></i>
            Book & add menu items
          </button>
          <p className="ff-resv-note">
            You can add a pre-order from the full menu after booking — prepay or pay at the restaurant.
            Free cancellation with a full refund any time before you are seated.
          </p>
        </div>
      )}
      <Footer />
    </>
  );
};

export default ReservationBookingPage;
